// Écran 1 — le brief du matin. Tout ce qui s'enregistre se fait d'ici en deux taps au plus :
// « FAITE » puis le ressenti pour la séance, « +15 » pour le tapis, « FAIT » pour l'école ou un rappel.
// Chaque action propose « Annuler » dans le message qui suit. Les propositions d'Alfred s'y tranchent aussi.

import { type ReactNode, useEffect, useState } from 'react';

import type { ItemAgenda, Ressenti } from '@alfred/shared';
import type { PropsEcran } from '../App';
import { Cloche, Fenetre, Icone, Nav, Pastille } from '../components';
import { compteARebours, ecartJours, jjmm, jourCourt, jourMois } from '../lib/format';
import { chargerQuete, dureeMin, sauverQuete } from '../lib/quete';
import { VOCAB } from '../lib/theme';
import { BandeauInstallation } from './Installer';

export function useHorloge(): string {
  const [maintenant, setMaintenant] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setMaintenant(new Date()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  return `${String(maintenant.getHours()).padStart(2, '0')}:${String(maintenant.getMinutes()).padStart(2, '0')}`;
}

/** `atelier` : l’entrée de la console des agents, sur une instance auto-hébergée seulement. */
/** `questionDuJour` : la carte de l'entretien (lot 3.5d), fournie par l'app pour ne pas lier Brief à l'entretien. */
export function Brief({ etat, actions, atelier = false, questionDuJour }: PropsEcran & { atelier?: boolean; questionDuJour?: ReactNode }) {
  const heure = useHorloge();
  const [feuille, setFeuille] = useState(false);
  const [courte, setCourte] = useState(false);
  const s = etat.seance;
  const prenom = etat.profil.prenom;

  useEffect(() => {
    // La version choisie à l'acceptation (écran Notification) est reprise dans la feuille.
    setCourte(chargerQuete(s)?.version === 'courte');
  }, [s, feuille]);

  const valider = async (ressenti: Ressenti) => {
    if (!s) return;
    const quete = chargerQuete(s);
    const duree = quete ? dureeMin(quete, Date.now()) : null;
    const ok = await actions.seance(
      { date: s.date, lettre: s.lettre, version: courte ? 'courte' : 'complete', ressenti, ...(duree ? { dureeMin: duree } : {}) },
      `Quête accomplie : Séance ${s.lettre}.`,
    );
    if (ok) {
      sauverQuete(null);
      setFeuille(false);
    }
  };

  const fait = (i: ItemAgenda) => void actions.rappelFait(i, `Fait : ${i.titre}`);

  // La séance du jour est déjà la quête : son rappel d'agenda ferait doublon.
  const alertes = [...etat.alertes.aujourdhui, ...etat.alertes.aVenir].filter((i) => !(i.rappel && i.categorie === 'sport')).slice(0, 6);

  let message;
  if (s && s.faite) message = <>Quête du jour accomplie.<br />Bien joué, {VOCAB.joueur} <strong>{prenom}</strong>.</>;
  else if (s && s.repli) message = <>Bonjour, {VOCAB.joueur} <strong>{prenom}</strong>.<br />Une quête d'hier attend encore.</>;
  else if (s) message = <>Bonjour, {VOCAB.joueur} <strong>{prenom}</strong>.<br />Une quête quotidienne est arrivée.</>;
  else message = <>Bonjour, {VOCAB.joueur} <strong>{prenom}</strong>.<br />Pas de séance aujourd'hui : les quêtes quotidiennes t'attendent.</>;

  const pct = etat.semaine.prevues ? (etat.semaine.faites / etat.semaine.prevues) * 100 : 0;

  return (
    <main className="ecran avec-nav">
      <div className="entete">
        <div className="marque">
          <span className="accent" style={{ display: 'flex' }}><Icone nom="hexagone" taille={26} /></span>
          <span>{VOCAB.systeme}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="mono muted" style={{ fontSize: 12, letterSpacing: 1 }}>{jourMois(etat.date)} · {heure}</span>
          {atelier && <a className="carre44" href="#/atelier" aria-label="Atelier"><Icone nom="atelier" /></a>}
          <Cloche />
          <a className="carre44" href="#/reglages" aria-label="Réglages"><Icone nom="reglages" /></a>
        </div>
      </div>

      {etat.erreurs.length > 0 && (
        <a className="bandeau-erreur" href="#/reglages">⚠ {etat.erreurs.length} élément(s) illisible(s) dans le cerveau : voir le détail.</a>
      )}

      <BandeauInstallation />

      <a href={s && !s.faite ? '#/notification' : '#/'} style={{ color: 'inherit' }}>
        <Fenetre>
          <div className="pastille-ligne">
            <span className="exclam">!</span>
            <Pastille>{VOCAB.notification}</Pastille>
          </div>
          <p className="message">{message}</p>
        </Fenetre>
      </a>

      {questionDuJour}

      <Propositions etat={etat} actions={actions} />

      <div className="joueur">
        <div className="niveau"><small>{VOCAB.niveau}</small><b>{etat.statut.niveau}</b></div>
        <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <b style={{ fontSize: 16, letterSpacing: 1 }}>{prenom.toUpperCase()}</b>
            <span className="muted" style={{ fontSize: 12 }}>
              {etat.phase ? `Phase ${etat.phase.numero} · ${etat.phase.nom}${etat.phase.semaineCourante ? ` · S${etat.phase.semaineCourante}` : ''}` : ''}
            </span>
          </div>
          <div className="jauge"><div style={{ width: `${pct}%` }} /></div>
          <div className="mono muted" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
            <span>Séances semaine</span><span>[{etat.semaine.faites} / {etat.semaine.prevues}]</span>
          </div>
        </div>
      </div>

      <div className="section">
        <span className="titre">{VOCAB.queteDuJour}</span>
        {s && <a href="#/quete" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Ouvrir <Icone nom="suivant" taille={14} /></a>}
      </div>

      <div className="lignes">
        {s && (
          <div className={`ligne${s.faite ? ' accomplie' : ''}`}>
            <a className="corps" href="#/quete" style={{ color: 'inherit' }}>
              <b>Séance {s.lettre} · {s.seance.titre}</b>
              <small>
                {s.faite
                  ? `Accomplie · ${s.faite.duree} · ${s.faite.ressenti}`
                  : s.repli
                    ? `À rattraper · créneau du ${jourCourt(s.date).toLowerCase()}`
                    : `${s.heure} · ≈ ${s.seance.duree_min} min · repli 12 min`}
              </small>
            </a>
            {s.faite ? <span className="compteur" style={{ padding: '0 8px' }}>[✓]</span> : <button className="btn principal" style={{ fontSize: 13 }} onClick={() => setFeuille(true)}>FAITE</button>}
          </div>
        )}

        <LignesQuotidien etat={etat} actions={actions} />
      </div>

      <EnRetard etat={etat} actions={actions} />

      <div className="section">
        <span className="titre">{VOCAB.alertes}</span>
        <a href="#/portails">14 jours</a>
      </div>
      <div className="alertes">
        {alertes.length === 0 && <p className="muted" style={{ fontSize: 13 }}>[Aucune alerte dans les prochains jours]</p>}
        {alertes.map((i) => {
          const n = ecartJours(etat.date, i.jour);
          const quand = n === 0 ? i.heure || 'AUJ.' : n === 1 ? 'DEMAIN' : n <= 6 ? jourCourt(i.jour) : compteARebours(etat.date, i.jour);
          return (
            <div key={`${i.titre}-${i.jour}`} className="alerte">
              <span className={`quand${n === 0 ? ' auj' : ''}`}>{quand}</span>
              <span className="quoi">{n !== 0 && i.heure ? `${i.heure} · ` : ''}{i.titre}{i.detail ? ` (${i.detail})` : ''}</span>
              {/* Un événement iCloud garde le nom et la couleur de son calendrier, comme dans les portails. */}
              {i.etiquette && <span className="tag" style={i.couleur ? { color: i.couleur } : undefined}>{i.etiquette}</span>}
              {n === 0 && i.rappel && <button className="btn petit" onClick={() => fait(i)}>FAIT</button>}
            </div>
          );
        })}
      </div>

      {feuille && s && (
        <div className="voile" onClick={() => setFeuille(false)}>
          <div className="feuille" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Séance faite">
            <p className="message">Séance <strong>{s.lettre} · {s.seance.titre}</strong> faite.<br /><span className="muted" style={{ fontSize: 13 }}>Ressenti ?</span></p>
            <div className="ressentis" role="group" aria-label="Version">
              <button aria-pressed={!courte} onClick={() => setCourte(false)}>Complète</button>
              <button aria-pressed={courte} onClick={() => setCourte(true)}>Version 12 min</button>
            </div>
            <div className="boutons">
              <button className="btn principal" onClick={() => void valider('facile')}>Facile</button>
              <button className="btn principal" onClick={() => void valider('correct')}>Correct</button>
              <button className="btn principal" onClick={() => void valider('dur')}>Dur</button>
            </div>
            <button className="btn fantome" onClick={() => setFeuille(false)}>Annuler</button>
          </div>
        </div>
      )}

      <Nav actif="brief" />
    </main>
  );
}

// Les blocs du brief que le tableau de bord du bureau reprend tels quels.

/** Les propositions d'Alfred, à accepter ou refuser. */
export function Propositions({ etat, actions }: PropsEcran) {
  if (etat.propositions.length === 0) return null;
  return (
    <>
      <div className="section"><span className="titre">LE SYSTÈME PROPOSE</span></div>
      {etat.propositions.map((p) => (
        <Fenetre key={p.id} equerres>
          <p className="message" style={{ fontSize: 14 }}>[<strong>{p.titre}</strong>]</p>
          {p.detail && <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-line' }}>{p.detail}</p>}
          <div className="boutons">
            <button className="btn principal" onClick={() => void actions.repondre(p.id, true)}>{VOCAB.accepter}</button>
            <button className="btn" onClick={() => void actions.repondre(p.id, false)}>Refuser</button>
          </div>
        </Fenetre>
      ))}
    </>
  );
}

/** Les quêtes quotidiennes (tapis, école) et la pesée du lundi, chacune avec son geste. */
export function LignesQuotidien({ etat, actions }: PropsEcran) {
  return (
    <>
      {etat.quotidien.map((q) => (
        <div key={q.id} className={`ligne${q.minutes >= q.objectifMin ? ' accomplie' : ''}`}>
          <div className="corps"><b>{q.titre}</b><small>{q.consigne}</small></div>
          <span className={`compteur${q.minutes ? '' : ' vide'}`}>[{q.minutes} / {q.objectifMin}]</span>
          {q.id === 'tapis' ? (
            <button
              className="btn petit"
              aria-label="Ajouter 15 minutes de tapis"
              onClick={() => void actions.marche({ date: etat.date, tapisAjoutMin: 15 }, 'Tapis : +15 min.')}
            >+15</button>
          ) : q.minutes ? (
            q.annulable !== null && <button className="btn petit fantome" onClick={() => void actions.annuler(q.annulable!, 'Annulé.')}>Annuler</button>
          ) : (
            <button className="btn petit" onClick={() => void actions.marche({ date: etat.date, ecole: true }, `${q.titre} : fait.`)}>FAIT</button>
          )}
        </div>
      ))}

      {etat.peseeDue && (
        <a className="ligne" href="#/pesee" style={{ color: 'inherit' }}>
          <span className="corps"><b>Pesée du lundi</b><small>À jeun, avant le petit-déjeuner</small></span>
          <span className="compteur vide" style={{ padding: '0 8px' }}>[ — kg ]</span>
        </a>
      )}
    </>
  );
}

/** Les rappels passés non faits. */
export function EnRetard({ etat, actions }: PropsEcran) {
  if (etat.alertes.enRetard.length === 0) return null;
  return (
    <>
      <div className="section"><span className="titre warn">{VOCAB.enRetard}</span></div>
      <div className="alertes">
        {etat.alertes.enRetard.map((i) => (
          <div key={`${i.titre}-${i.jour}`} className="alerte retard">
            <span className="quand">{jjmm(i.jour)}</span>
            <span className="quoi">{i.titre}</span>
            {i.rappel && <button className="btn petit" onClick={() => void actions.rappelFait(i, `Fait : ${i.titre}`)}>FAIT</button>}
          </div>
        ))}
      </div>
    </>
  );
}
